import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Truck, ChevronLeft, ChevronRight, Heart, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtBRL, buildWhatsAppUrl, buildProductInterestMessage } from "@/lib/format";
import { imgVariant, imgSrcSet } from "@/lib/img";
import { cashPrice, installmentLabel } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { trackWaClick } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";

/**
 * Galeria do card de produto — vale automaticamente para todos os produtos:
 * - hover mostra a 2ª foto (volta ao sair)
 * - setas laterais navegam por todas as fotos (sem abrir o produto)
 * - arrastar com o mouse / deslizar com o dedo
 * - indicadores de fotos abaixo da imagem
 * - clique simples continua abrindo a página do produto
 */
const CardGallery = ({ product, override }) => {
  const navigate = useNavigate();
  const settings = useSettings();
  const base = product.images?.length ? product.images : ["/assets/logo.png"];
  const imgs = override?.length ? override : base;
  const total = imgs.length;
  const isFallback = !product.images?.length && !override?.length;

  const [activeIdx, setActiveIdx] = useState(0);
  const [hovering, setHovering] = useState(false);
  const dragX = useRef(null);
  const dragged = useRef(false);
  // arraste das galerias — desligável no painel (Animações e Efeitos)
  const dragOn = (settings.animations || {}).enabled !== false && (settings.animations || {}).gallery_drag !== false;

  // hover mostra a 2ª foto apenas quando o cliente está na 1ª (setas têm prioridade)
  const hoverOn = (settings.animations || {}).enabled !== false && (settings.animations || {}).product_hover !== false;
  const shown = hoverOn && hovering && total > 1 && activeIdx === 0 ? 1 : activeIdx;

  const go = (dir, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (total < 2) return;
    setActiveIdx((i) => (i + dir + total) % total);
  };

  const onPointerDown = (e) => {
    if (!dragOn) return;
    dragX.current = e.clientX;
    dragged.current = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* sem captura em alguns navegadores */
    }
  };
  const onPointerMove = (e) => {
    if (dragX.current == null) return;
    if (Math.abs(e.clientX - dragX.current) > 8) dragged.current = true;
  };
  const onPointerUp = (e) => {
    if (dragX.current == null) return;
    const dx = e.clientX - dragX.current;
    dragX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };
  const onClick = () => {
    if (dragged.current) {
      dragged.current = false;
      return; // foi arrasto, não clique
    }
    navigate(`/produto/${product.id}`);
  };

  return (
    <div
      className="relative bg-white aspect-[4/3] overflow-hidden group/gallery cursor-pointer select-none"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (dragX.current = null)}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/produto/${product.id}`)}
      role="link"
      tabIndex={0}
      aria-label={`Ver detalhes de ${product.name}`}
      style={{ touchAction: "pan-y" }}
      data-testid={`card-gallery-${product.id}`}
    >
      {imgs.map((img, i) => (
        <img
          key={i}
          src={imgVariant(img, "card")}
          srcSet={imgSrcSet(img)}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={i === 0 ? product.name : `${product.name} — foto ${i + 1}`}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          draggable={false}
          data-testid={i === 0 ? `product-image-${product.id}` : undefined}
          className={`absolute inset-0 w-full h-full object-contain ${isFallback ? "p-10" : "p-2"} transition-opacity duration-300 ${
            i === shown ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      {product.is_demo && (
        <span className="absolute top-2 right-2 z-10 bg-slate-800/80 text-white text-[10px] font-medium rounded-full px-2 py-0.5">
          Demonstração
        </span>
      )}
      {product.stock <= 0 && (
        <span className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center font-heading font-bold text-gray-600">
          Indisponível
        </span>
      )}

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => go(-1, e)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Foto anterior"
            className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-700 opacity-70 sm:opacity-0 sm:group-hover/gallery:opacity-100 transition-opacity"
            data-testid={`gallery-prev-${product.id}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => go(1, e)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Próxima foto"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-700 opacity-70 sm:opacity-0 sm:group-hover/gallery:opacity-100 transition-opacity"
            data-testid={`gallery-next-${product.id}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20 flex gap-1">
            {imgs.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveIdx(i);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={`Ir para a foto ${i + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === shown ? "bg-white ring-1 ring-black/20" : "bg-white/50"
                }`}
                data-testid={`gallery-dot-${product.id}-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const settings = useSettings();
  const { favorites, toggleFavorite } = useCustomer();
  const isFav = favorites.includes(product.id);
  const out = product.stock <= 0;
  // selo de desconto: % manual do painel ou calculada da promoção real; nunca inventada
  const realPct = product.promo_price && product.promo_price < product.price
    ? Math.round((1 - product.promo_price / product.price) * 100)
    : 0;
  const badgePct = product.discount_badge === false ? 0 : (product.discount_percent > 0 && realPct > 0 ? product.discount_percent : realPct);
  const variants = product.color_variants || [];
  const [color, setColor] = useState(null); // null = galeria geral do produto
  const selectedVariant = variants.find((v) => v.name === color);
  const colorUnavailable = !!selectedVariant && selectedVariant.available === false;
  // cor padrão do card: primeira variação disponível (nunca uma esgotada)
  const firstAvailable = variants.length
    ? variants.find((v) => v.available !== false)?.name || null
    : product.colors?.[0] || null;
  const galleryOverride = selectedVariant?.images?.length
    ? selectedVariant.main_image
      ? [selectedVariant.main_image, ...selectedVariant.images.filter((i) => i !== selectedVariant.main_image)]
      : selectedVariant.images
    : null;

  return (
    <div
      className="group relative bg-white border border-gray-100 rounded-xl overflow-hidden card-hover flex flex-col"
      data-testid={`product-card-${product.id}`}
      data-anim="card"
    >
      <CardGallery key={color || "geral"} product={product} override={galleryOverride} />
      {badgePct > 0 && (
        <span
          className="absolute top-2 left-2 z-10 bg-secondary text-slate-900 text-xs font-extrabold rounded-full px-2.5 py-1 shadow-sm"
          data-testid={`discount-badge-${product.id}`}
        >
          -{badgePct}%
        </span>
      )}
      {(product.sales_count || 0) > 0 && (
        <span
          className="absolute bottom-2 left-2 z-10 bg-brand text-white text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 shadow-sm"
          data-testid={`bestseller-badge-${product.id}`}
        >
          Mais Vendido
        </span>
      )}
      <button
        type="button"
        onClick={async () => {
          const fav = await toggleFavorite(product.id);
          toast.success(fav ? "Adicionado aos favoritos" : "Removido dos favoritos");
        }}
        aria-label={isFav ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        className="absolute top-2 right-2 z-30 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center transition-transform active:scale-90"
        data-testid={`favorite-${product.id}`}
      >
        <Heart className={`w-4 h-4 transition-colors ${isFav ? "fill-red-500 text-red-500" : "text-gray-400"}`} />
      </button>

      <div className="p-3 sm:p-4 flex flex-col flex-1">
        {/* amostras de cor no card */}
        {variants.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5" data-testid={`card-colors-${product.id}`}>
            {variants.map((v) => {
              const thumb = v.main_image || v.images?.[0];
              return (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => setColor(color === v.name ? null : v.name)}
                  title={v.name + (v.available === false ? " (esgotada)" : "")}
                  aria-label={`Ver na cor ${v.name}`}
                  className={`w-6 h-6 rounded-full overflow-hidden border-2 transition-all ${
                    color === v.name ? "border-brand scale-110 shadow" : "border-gray-200 hover:border-brand/50"
                  } ${v.available === false ? "opacity-40" : ""}`}
                  data-testid={`card-color-${product.id}-${v.name.toLowerCase()}`}
                >
                  {thumb ? (
                    <img src={imgVariant(thumb, "thumb")} alt={v.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <span className="block w-full h-full bg-gradient-to-br from-slate-100 to-slate-300" />
                  )}
                </button>
              );
            })}
            {color && <span className="text-[10px] text-gray-500 ml-0.5">{color}</span>}
          </div>
        )}
        <Link to={`/produto/${product.id}`}>
          <h3 className="font-medium text-base sm:text-lg text-gray-800 line-clamp-2 min-h-[3rem] sm:min-h-[3.5rem] hover:text-brand transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-2">
          <span className="font-heading font-extrabold text-xl sm:text-2xl text-[#D71920] tracking-tight" data-testid={`price-${product.id}`}>
            {fmtBRL(cashPrice(product))}
          </span>
          <span className="block text-xs sm:text-sm text-gray-500 mt-0.5" data-testid={`installments-${product.id}`}>
            Ou {installmentLabel(product)} no cartão de crédito
          </span>
        </div>

        <div className="flex items-center gap-1 mt-2 text-[11px] text-green-700 font-medium">
          <Truck className="w-3.5 h-3.5" /> Pagamento na entrega
        </div>

        <div className="mt-3 flex gap-1.5">
          <Button
            className="flex-1 rounded-full active:scale-95 transition-transform"
            size="sm"
            disabled={out || colorUnavailable}
            onClick={() => addItem(product, color || firstAvailable, 1)}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4" /> {colorUnavailable ? "Cor esgotada" : "Adicionar"}
          </Button>
          <a
            href={buildWhatsAppUrl(
              buildProductInterestMessage({ product, color: color || firstAvailable, domain: settings.company_domain }),
              settings.whatsapp_number
            )}
            target="_blank"
            rel="noreferrer"
            aria-label={`Comprar ${product.name} pelo WhatsApp`}
            data-testid={`wa-buy-${product.id}`}
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all"
            onClick={(e) => { e.stopPropagation(); trackWaClick(product.id, "card"); }}
          >
            <MessageCircle className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};
